import ArrowSVG from '@public/icons/arr.svg';
import classNames from 'classnames';
import { AnimatePresence, m as motion } from 'framer-motion';
import { useCallback, useMemo, useState } from 'react';
import { Place } from '@/entities/place';
import { useSetOpenedPopupsState } from '@/shared/atoms/opened-popups';
import { AppConstructor } from '@/shared/components/constructor/Constructor';
import LiveProgramPopup, {
    LIVE_PROGRAM_POPUP_NAME,
} from '@/shared/components/popups/LiveProgramPopup/LiveProgramPopup';
import SchemaPopup from '@/shared/components/popups/SchemaPopup/SchemaPopup';
import Dropdown from '@/shared/components/shared/Dropdown';
import { DICT } from '@/shared/config/dict';
import { useLocale } from '@/shared/hooks/use-locale';
import { ImageShape, ParticipationFormat } from '@/shared/lib/types';
import { tp } from '@/shared/lib/typograf';
import { easeOutQuart } from '@/shared/lib/utils/easings';
import Button from '../../shared/Button';
import Image from '../../shared/Image';
import { ParticipationFormatsCircles } from '../../shared/ParticipationFormatsCircles';
import Typografed from '../../shared/Typografed';

export type LiveProgramItem = {
    id: number;
    title: string;
    prefix: string;
    date: string;
    datetime: string;
    access: string[];
    participationFormats: ParticipationFormat[];
    place: Place | null;
    tags: string[];
    description: string;
    content: AppConstructor;
    image: ImageShape | null;
    images: ImageShape[];
};

export type LiveProgramSectionProps = {
    title: string;
    description: string;
    items: LiveProgramItem[];
};

type Props = React.HTMLAttributes<HTMLElement> & {
    liveProgramSectionProps: LiveProgramSectionProps;
};

const CARD_TRANSITION = { duration: 0.4, ease: easeOutQuart };

const uniqueInOrder = (values: string[]) => {
    const seen = new Set<string>();

    return values.filter((value) => {
        if (seen.has(value)) {
            return false;
        }

        seen.add(value);
        return true;
    });
};

const LiveProgramSection = ({ liveProgramSectionProps, ...props }: Props) => {
    const { items, title, description } = liveProgramSectionProps;
    const [selectedDate, setSelectedDate] = useState<string | null>(null);
    const [selectedAccess, setSelectedAccess] = useState<string[]>([]);
    const [activeItem, setActiveItem] = useState<LiveProgramItem | null>(null);

    const dates = useMemo(() => uniqueInOrder(items.map((item) => item.date)), [items]);
    const accessOptions = useMemo(() => uniqueInOrder(items.flatMap((item) => item.access)), [items]);

    const filteredItems = useMemo(
        () =>
            items.filter((item) => {
                const matchesDate = !selectedDate || item.date === selectedDate;
                const matchesAccess =
                    selectedAccess.length === 0 ||
                    item.access.length === 0 ||
                    item.access.includes('Все') ||
                    item.access.some((access) => selectedAccess.includes(access));

                return matchesDate && matchesAccess;
            }),
        [items, selectedDate, selectedAccess],
    );

    const handleDateChange = useCallback((date: string | null) => {
        setSelectedDate((prev) => (date && date !== prev ? date : null));
    }, []);

    const handleAccessChange = useCallback((access: string) => {
        setSelectedAccess((prev) =>
            prev.includes(access) ? prev.filter((item) => item !== access) : [...prev, access],
        );
    }, []);

    return (
        <section
            {...props}
            className={classNames('live-program-section js-header-theme-trigger', props.className)}
            data-theme="light"
        >
            <div className="live-program-section__wrapper wrapper">
                {(title || description) && (
                    <div className="live-program-section__header">
                        {title && (
                            <h2
                                className="live-program-section__title h2"
                                dangerouslySetInnerHTML={{ __html: tp(title) }}
                            />
                        )}
                        {/* TODO если согласуют выпилить на беке */}
                        {/* {description && (
                            <div
                                className="live-program-section__description text-s text-opacity"
                                dangerouslySetInnerHTML={{ __html: tp(description) }}
                            />
                        )} */}
                    </div>
                )}

                <div className="program-filters live-program-section__filters">
                    <FiltersDefault
                        dates={dates}
                        accessOptions={accessOptions}
                        selectedDate={selectedDate}
                        selectedAccess={selectedAccess}
                        handleDateChange={handleDateChange}
                        handleAccessChange={handleAccessChange}
                    />
                    <FiltersMobile
                        dates={dates}
                        accessOptions={accessOptions}
                        selectedDate={selectedDate}
                        selectedAccess={selectedAccess}
                        handleDateChange={handleDateChange}
                        handleAccessChange={handleAccessChange}
                    />
                </div>

                <ul className="live-program-section__list list-unstyled">
                    <AnimatePresence mode="popLayout" initial={false}>
                        {filteredItems.length > 0 ? (
                            filteredItems.map((item) => (
                                <LiveProgramCard key={item.id} item={item} setActiveItem={setActiveItem} />
                            ))
                        ) : (
                            <motion.li
                                key="empty"
                                className="live-program-section__empty-item"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={CARD_TRANSITION}
                            >
                                <p className="live-program-section__empty text-l">
                                    <Typografed>
                                        Ничего не найдено. Попробуйте изменить запрос и мы поищем еще раз.
                                    </Typografed>
                                </p>
                            </motion.li>
                        )}
                    </AnimatePresence>
                </ul>
            </div>
            <LiveProgramPopup item={activeItem} />
            {activeItem?.place && (
                <SchemaPopup
                    image={activeItem.place.image}
                    isBackBtn
                    content={{ name: activeItem.place.name, description: activeItem.description }}
                />
            )}
        </section>
    );
};

type FiltersProps = {
    dates: string[];
    accessOptions: string[];
    selectedDate: string | null;
    selectedAccess: string[];
    handleDateChange: (date: string | null) => void;
    handleAccessChange: (access: string) => void;
};

const FiltersDefault = ({
    dates,
    accessOptions,
    selectedDate,
    selectedAccess,
    handleDateChange,
    handleAccessChange,
}: FiltersProps) => {
    const locale = useLocale();

    return (
        <div className="program-filters__default">
            {dates.length > 0 && (
                <div className="program-filters__group program-filters__group--noshrink">
                    <p className="program-filters__group-title text-xs text-opacity">{tp(DICT.FORUM_DAYS[locale])}</p>
                    <div className="program-filters__group-inner">
                        {dates.map((date) => (
                            <Button
                                key={date}
                                type="button"
                                className={classNames({ active: date === selectedDate })}
                                variant="grey-rounded-checkable"
                                onClick={() => handleDateChange(date)}
                            >
                                {date}
                            </Button>
                        ))}
                    </div>
                </div>
            )}
            {accessOptions.length > 0 && (
                <div className="program-filters__group">
                    <p className="program-filters__group-title text-xs text-opacity">
                        {tp(DICT.PARTICIPATION_FORMAT[locale])}:
                    </p>
                    <div className="program-filters__group-inner program-filters__group-inner--wrap">
                        {accessOptions.map((access) => (
                            <Button
                                key={access}
                                type="button"
                                className={classNames({ active: selectedAccess.includes(access) })}
                                variant="grey-rounded-checkable"
                                onClick={() => handleAccessChange(access)}
                            >
                                {access}
                            </Button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const FiltersMobile = ({
    dates,
    accessOptions,
    selectedDate,
    selectedAccess,
    handleDateChange,
    handleAccessChange,
}: FiltersProps) => {
    const locale = useLocale();

    return (
        <div className="program-filters__mobile">
            {dates.length > 0 && (
                <Dropdown>
                    <Dropdown.Toggler className="news-select-btn">
                        <div className="text-xs">{selectedDate || tp(DICT.ALL_DAYS[locale])}</div>
                        <ArrowSVG className="news-arrow-svg" />
                    </Dropdown.Toggler>
                    <Dropdown.Content className="news-filter__content" position="bottom-right">
                        <ul className="list-unstyled menu-dropdown-list">
                            <li>
                                <Dropdown.Picker
                                    onClick={() => handleDateChange(null)}
                                    className={classNames('news-select-item text-xs', {
                                        'is-active': !selectedDate,
                                    })}
                                >
                                    {tp(DICT.ALL_DAYS[locale])}
                                </Dropdown.Picker>
                            </li>
                            {dates.map((date) => (
                                <li key={date}>
                                    <Dropdown.Picker
                                        onClick={() => handleDateChange(date)}
                                        className={classNames('news-select-item text-xs', {
                                            'is-active': selectedDate === date,
                                        })}
                                    >
                                        {date}
                                    </Dropdown.Picker>
                                </li>
                            ))}
                        </ul>
                    </Dropdown.Content>
                </Dropdown>
            )}
            {accessOptions.length > 0 && (
                <Dropdown>
                    <Dropdown.Toggler className="news-select-btn">
                        <div className="text-xs">{tp(DICT.PARTICIPATION_FORMAT[locale])}</div>
                        <ArrowSVG className="news-arrow-svg" />
                    </Dropdown.Toggler>
                    <Dropdown.Content className="news-filter__content" position="bottom-right">
                        <ul className="list-unstyled menu-dropdown-list">
                            {accessOptions.map((access) => (
                                <li key={access}>
                                    <Dropdown.Picker
                                        className={classNames('news-select-item text-xs', {
                                            'is-active': selectedAccess.includes(access),
                                        })}
                                        onClick={() => handleAccessChange(access)}
                                        keepOpen
                                    >
                                        {access}
                                    </Dropdown.Picker>
                                </li>
                            ))}
                        </ul>
                    </Dropdown.Content>
                </Dropdown>
            )}
        </div>
    );
};

const LiveProgramCard = ({
    item,
    setActiveItem,
}: {
    item: LiveProgramItem;
    setActiveItem: (item: LiveProgramItem) => void;
}) => {
    const { openPopup } = useSetOpenedPopupsState();
    const accessLabel = item.access.join(', ');
    const time = item.datetime.startsWith(item.date) ? item.datetime.slice(item.date.length).trim() : item.datetime;

    return (
        <motion.li
            className="live-program-section__item"
            layout="position"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={CARD_TRANSITION}
        >
            <button
                className="live-program-section__card"
                type="button"
                onClick={() => {
                    setActiveItem(item);
                    openPopup(LIVE_PROGRAM_POPUP_NAME);
                }}
                aria-label={`Открыть ${item.title}`}
            >
                {item.image?.src && (
                    <div className="live-program-section__card-image">
                        <Image
                            src={item.image.src}
                            alt={item.image.alt || item.title}
                            width={item.image.width ?? 230}
                            height={item.image.height ?? 320}
                            sizes="(max-width: 767px) 104px, 230px"
                        />
                    </div>
                )}
                <div className="live-program-section__card-content">
                    <div className="live-program-section__card-field">
                        {item.prefix && (
                            <span className="live-program-section__card-prefix text-s text-opacity">
                                <Typografed>{item.prefix}</Typografed>
                            </span>
                        )}
                        <span className="live-program-section__card-value text-m">
                            <Typografed>{item.title}</Typografed>
                        </span>
                    </div>
                    {(item.date || time) && (
                        <div className="live-program-section__card-field">
                            {/* <span className="live-program-section__card-label text-xs">Дата и время</span> */}
                            {item.date && (
                                <span className="live-program-section__card-value text-s">
                                    <Typografed>{item.date}</Typografed>
                                </span>
                            )}
                            {time && (
                                <span className="live-program-section__card-value text-s">
                                    <Typografed>{time}</Typografed>
                                </span>
                            )}
                        </div>
                    )}
                    {(item.participationFormats.length > 0 || accessLabel) && (
                        <div className="live-program-section__card-field">
                            <span className="live-program-section__card-label text-xs">Доступ</span>
                            {item.participationFormats.length > 0 ? (
                                <ParticipationFormatsCircles formats={item.participationFormats} />
                            ) : (
                                <span className="live-program-section__card-value text-s">
                                    <Typografed>{accessLabel}</Typografed>
                                </span>
                            )}
                        </div>
                    )}
                    {item.place?.name && (
                        <div className="live-program-section__card-field">
                            <span className="live-program-section__card-label text-xs">Место проведения</span>
                            <span className="live-program-section__card-value text-s">
                                <Typografed>{item.place.name}</Typografed>
                            </span>
                        </div>
                    )}
                </div>
            </button>
        </motion.li>
    );
};

export default LiveProgramSection;
